import { skipToken } from "@reduxjs/toolkit/query";
import {
  asPlaybookId,
  checkPlaybookHasKnowledgeBaseAdherence,
  getOutcomeDefinitionFromPlaybook,
} from "@repo/app-types/entities/playbook";
import { asTeammateId } from "@repo/app-types/entities/teammate";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { PageHeaderFilterBarPortal } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { useMemo, type FC } from "react";
import { Heading } from "react-aria-components";
import { useParams } from "react-router";
import {
  usePlaybookTeammatePerformanceQuery,
  usePlaybookTeammatePlaybookCriteriaScoresQuery,
  usePlaybookTeammatePlaybooksQuery,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useTeammateQuery } from "../../api/entities/teammate.js";
import { PageBody } from "../../components/layout/pageLayout.js";
import { PerformancePlaybookSwitcher } from "../../components/performancePlaybookSwitcher.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { NavigationSlideoverContainer } from "../../hooks/useModalNavigation.js";
import { AnalyticsProvider } from "../analytics/analyticsContext/analyticsProvider.js";
import {
  buildTeamPerformancePageQueryArgs,
  dateRangeToISORange,
} from "../filters/buildFilterQueryArgs.js";
import { useFilters } from "../filters/filters.js";
import { KnowledgeBaseAdherenceStatsCard } from "./knowledgeBaseAdherenceStatsCard.js";
import { KnowledgeBaseAdherenceThemesCard } from "./knowledgeBaseAdherenceThemesCard.js";
import { TeammateCoachingSection } from "./teammateCoachingSection.js";
import { TeammateStatsTable } from "./teammateStatsTable.js";
import { TrainingParticipantsSection } from "./training/trainingParticipantsSection.js";
import { TrainingTeammatePagePlaybook } from "./trainingTeammatePagePlaybook.js";

/**
 * Page accessible via https://<host>/performance/playbooks/<playbookId>/teammates/<teammateId>
 */
export const TrainingTeammatePageOverview: FC = () => {
  const { playbookId: playbookIdParam, teammateId: teammateIdParam } =
    useParams();
  if (!playbookIdParam || !teammateIdParam) {
    throw new Error("Missing param playbookId or teammateId.");
  }
  const playbookId = asPlaybookId(playbookIdParam);

  const teammateId = asTeammateId(teammateIdParam);
  const teammatePageAnalyticsScope = useMemo(
    () => ({ teammateId, playbookId }),
    [playbookId, teammateId],
  );

  const now = useNow();

  const [
    {
      dateRange,
      teammateGroupIds,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      dateBasis,
    },
    renderFilters,
  ] = useFilters(
    [
      "dateRange",
      "teammateGroupIds",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "dateBasis",
    ],
    { playbookId, teammateId },
  );

  // Fetch the playbook first to get the outcome definition for filters
  const { data: playbook, isLoading: isLoadingPlaybook } =
    usePlaybookByIdQuery(playbookId);
  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

  // Playbooks the teammate has calls on within the active date range — used to
  // scope the playbook switcher's options to a relevant subset.
  const { data: teammatePlaybookIds } = usePlaybookTeammatePlaybooksQuery({
    teammateId,
    dateRange: dateRangeToISORange(now, dateRange),
  });

  const baseArgs =
    teammateGroupIds !== undefined
      ? buildTeamPerformancePageQueryArgs(now, {
          playbookId,
          dateRange,
          teammateGroupIds,
          outcome,
          customFields,
          playbookConditions,
          signalId,
          signalMetadataFields,
          totalRecordingDuration,
          dateBasis,
        })
      : undefined;

  const queryArgs = baseArgs
    ? {
        playbookId,
        teammateId,
        scope: { ...baseArgs.scope, playbookId },
        filters: baseArgs.filters,
      }
    : skipToken;

  const {
    data: teammateData,
    isLoading: isLoadingTeammate,
    isFetching,
    isError: isTeammateError,
  } = usePlaybookTeammatePerformanceQuery(queryArgs);
  // On error, RTK Query's `data` keeps the last successful (possibly
  // differently-filtered) result — drop it so we render empty states instead
  // of stale numbers.
  const teammate = isTeammateError ? undefined : teammateData;

  // Fetch outcome score for this teammate
  const {
    data: criteriaScoresDataRaw,
    isLoading: isLoadingOtherScores,
    isError: isCriteriaScoresError,
  } = usePlaybookTeammatePlaybookCriteriaScoresQuery(queryArgs);
  const criteriaScoresData = isCriteriaScoresError
    ? undefined
    : criteriaScoresDataRaw;
  const playbookKnowledgeBaseAdherenceScore =
    criteriaScoresData?.knowledgeBaseScore;

  const { data: fullTeammate } = useTeammateQuery(teammateId);

  const vocalIntelligenceEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.VocalIntelligence,
  );
  const customerExperienceScoreEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.CustomerExperienceScore,
  );
  const storedVocalIntelligence =
    fullTeammate?.settings.vocal_intelligence.find(
      (entry) => entry.playbook_id === playbookId,
    );
  const vocalScore =
    vocalIntelligenceEnabled && storedVocalIntelligence
      ? storedVocalIntelligence.analysis
      : null;

  return (
    <AnalyticsProvider scope="teammatePage" value={teammatePageAnalyticsScope}>
      <PageHeaderFilterBarPortal>
        {renderFilters({
          isLoading: isFetching,
          leadingItems: (
            <PerformancePlaybookSwitcher
              staysOnFullPath
              selectablePlaybookIds={teammatePlaybookIds ?? []}
            />
          ),
        })}
      </PageHeaderFilterBarPortal>

      <PageBody>
        <div className="grid grid-cols-1 gap-6 min-[1200px]:grid-cols-[minmax(0,1fr)_400px]">
          {/* Keyed on both ids so local state (selected period, editor,
              expansion) resets when switching playbooks *or* teammates.
              Teammate matters as much as playbook: navigating between two
              teammates on the same playbook only changes a route param, and
              RTK Query keeps serving the previous teammate's coaching while
              the new request is in flight — a remount clears that, so the
              section can never render (or report) one teammate's goal under
              another's name. */}
          <TeammateCoachingSection
            key={`${playbookId}:${teammateId}`}
            playbookId={playbookId}
            teammateId={teammateId}
          />

          <TeammateStatsTable
            teammate={teammate}
            isLoadingTeammate={isLoadingTeammate}
            outcomeDefinition={
              isLoadingPlaybook ? undefined : outcomeDefinition
            }
            outcomeGreenRate={criteriaScoresData?.outcomeGreenRate}
            outcomeValueRateSparklines={
              criteriaScoresData?.outcomeValueRateSparklines
            }
            isLoadingOutcomeScore={isLoadingOtherScores}
            dateRangeEnd={dateRange.end}
            customerExperienceScoreEnabled={customerExperienceScoreEnabled}
            vocalScore={vocalScore}
          />
        </div>

        <TrainingParticipantsSection
          className="col-span-full"
          playbookId={playbookId}
          teammateId={teammateId}
        />

        {playbook !== undefined || isLoadingPlaybook ? (
          <div className="pb-4">
            <div className="mb-3 flex items-center gap-3">
              <Label size={Size.MEDIUM}>Playbook</Label>
            </div>
            <TrainingTeammatePagePlaybook
              playbookId={playbookId}
              teammateId={teammateId}
              getCriterionPath={(criterionId: string) =>
                `criteria/${encodeURIComponent(criterionId)}`
              }
            />
          </div>
        ) : null}

        {playbook !== undefined &&
        checkPlaybookHasKnowledgeBaseAdherence(playbook) ? (
          <section className="space-y-2">
            <Heading
              level={2}
              className="text-base font-semibold text-foreground"
            >
              Knowledge base adherence
            </Heading>

            <KnowledgeBaseAdherenceStatsCard
              data={playbookKnowledgeBaseAdherenceScore}
              isLoading={isLoadingOtherScores}
            />

            <KnowledgeBaseAdherenceThemesCard
              playbookId={playbookId}
              teammateId={teammateId}
            />
          </section>
        ) : null}

        <NavigationSlideoverContainer />
      </PageBody>
    </AnalyticsProvider>
  );
};
